import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../App";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Receipt, CheckCircle2, Upload, FileText, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const tipoCuentaLabel = (t) => ({
  transferencia: "Transferencia",
  pago_movil: "Pago Móvil",
  punto_venta: "Punto de Venta",
  deposito: "Depósito",
  corriente: "Corriente",
  ahorro: "Ahorro",
}[t] || t || "—");

export default function PortalReportarPagoPage() {
  const { token, API, user } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [deuda, setDeuda] = useState({ inmuebles: [], declaraciones_pendientes: [] });
  const [seleccion, setSeleccion] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmNro, setConfirmNro] = useState("");
  const [form, setForm] = useState({ banco_id:"", cuenta_recaudadora_id:"", metodo_pago:"transferencia", referencia:"", monto_pagado:"", fecha_pago:"", comprobante_url:"", observaciones:"" });
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/liquidaciones/mi-deuda`, auth).then(r => setDeuda(r.data));
    axios.get(`${API}/definiciones/bancos`, auth).then(r => setBancos(r.data));
    axios.get(`${API}/definiciones/cuentas-bancarias`, auth).then(r => setCuentas(r.data));
  // eslint-disable-next-line
  }, []);

  const fmt = n => `Bs. ${(Number(n)||0).toLocaleString("es-VE", { maximumFractionDigits: 2 })}`;
  // Unificar: liquidaciones legadas (por inmueble) + planillas del nuevo flujo (declaraciones)
  const allLiqs = [
    ...(deuda.inmuebles || []).flatMap(b => b.liquidaciones_pendientes),
    ...(deuda.declaraciones_pendientes || []),
  ];
  const totalSel = allLiqs.filter(l => seleccion.includes(l.id)).reduce((a,b) => a + (b.monto||0), 0);

  const toggle = (id) => setSeleccion(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  // Sube el comprobante al backend y guarda la URL en el form
  const handleUpload = async (file) => {
    if (!file) return;
    if (!["image/jpeg","image/png","image/jpg","application/pdf"].includes(file.type)) { toast.error("Use JPG, PNG o PDF"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Archivo supera 8 MB"); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      setUploading(true);
      const r = await axios.post(`${API}/recaudaciones/comprobante/upload`, fd, {
        headers: { Authorization: auth.headers.Authorization },
      });
      setForm(f => ({ ...f, comprobante_url: r.data.url }));
      toast.success("Comprobante subido");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir comprobante");
    } finally { setUploading(false); }
  };

  const fullComprobanteUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${url}`;
  };

  const submit = async () => {
    if (!seleccion.length) { toast.error("Seleccione liquidaciones a pagar"); return; }
    if (!form.fecha_pago) { toast.error("Indique la fecha del pago"); return; }
    if (!form.banco_id) { toast.error("Seleccione el Banco Emisor"); return; }
    if (!form.cuenta_recaudadora_id) { toast.error("Seleccione la Cuenta Recaudadora"); return; }
    const refClean = String(form.referencia || "").trim();
    if (!refClean) { toast.error("Indique la referencia bancaria"); return; }
    if (refClean.length < 6) { toast.error("La referencia debe tener al menos 6 caracteres"); return; }
    const monto = Number(form.monto_pagado) || totalSel;
    if (!monto || monto <= 0) { toast.error("Ingrese el monto pagado"); return; }
    if (!form.comprobante_url) { toast.error("Debe subir el comprobante de pago (JPG/PNG/PDF)"); return; }
    try {
      const r = await axios.post(`${API}/recaudaciones/pago`, {
        contribuyente_id: user?.contribuyente_id || "",
        liquidacion_ids: seleccion,
        monto_pagado: monto,
        metodo_pago: form.metodo_pago || "transferencia",
        banco_id: form.banco_id,
        cuenta_recaudadora_id: form.cuenta_recaudadora_id,
        comprobante_url: form.comprobante_url || null,
        referencia: refClean,
        fecha_pago: form.fecha_pago,
        observaciones: form.observaciones,
      }, auth);
      toast.success(`Pago reportado · ${r.data.nro_comprobante}`);
      setConfirmNro(r.data.nro_comprobante || "");
      setConfirmOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6" data-testid="portal-reportar-page">
      <div>
        <h1 className="font-display font-extrabold text-3xl text-green-900 flex items-center gap-3">
          <Receipt className="w-8 h-8 text-orange-500" />Reportar Pago
        </h1>
        <p className="text-green-700/70 mt-1">Pague vía transferencia/depósito y reporte aquí el comprobante</p>
      </div>

      <Card className="p-5 border-green-100">
        <h3 className="font-display font-bold text-lg text-green-900 mb-3">1. Seleccione las liquidaciones a pagar</h3>
        {!allLiqs.length ? (
          <p className="text-center py-8 text-slate-500">¡No tiene liquidaciones pendientes!</p>
        ) : (
          <div className="space-y-2">{allLiqs.map(l => (
            <label key={l.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${seleccion.includes(l.id) ? "bg-green-50 border-green-400" : "bg-white border-green-100 hover:bg-green-50/30"}`} data-testid={`portal-liq-${l.periodo}`}>
              <input type="checkbox" checked={seleccion.includes(l.id)} onChange={() => toggle(l.id)} className="w-4 h-4 accent-green-700" />
              <div className="flex-1">
                <p className="font-mono text-xs text-slate-500">{l.periodo} · {l.nro_planilla}</p>
                <p className="text-sm text-green-900 font-medium">{l.descripcion}</p>
              </div>
              <p className="font-mono font-semibold text-green-900">{fmt(l.monto)}</p>
            </label>
          ))}</div>
        )}
        {seleccion.length > 0 && (
          <div className="mt-4 p-3 bg-orange-50 rounded-lg border border-orange-100 flex items-center justify-between">
            <span className="text-sm font-medium text-orange-900">{seleccion.length} seleccionadas</span>
            <span className="font-display font-bold text-xl text-orange-700">{fmt(totalSel)}</span>
          </div>
        )}
      </Card>

      {seleccion.length > 0 && (
        <Card className="p-5 border-green-100">
          <h3 className="font-display font-bold text-lg text-green-900 mb-3">2. Datos del pago realizado</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Banco origen*</Label>
              <Select value={form.banco_id} onValueChange={v => setForm({...form, banco_id:v})}>
                <SelectTrigger data-testid="portal-banco-origen"><SelectValue placeholder="Seleccione banco" /></SelectTrigger>
                <SelectContent>{bancos.map(b => <SelectItem key={b.id} value={b.id}>{b.codigo} - {b.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Cuenta Recaudadora* <span className="text-[10px] text-slate-400">· cuentas de la alcaldía</span></Label>
              <Select value={form.cuenta_recaudadora_id} onValueChange={v => setForm({...form, cuenta_recaudadora_id:v})}>
                <SelectTrigger data-testid="portal-cuenta-recaudadora"><SelectValue placeholder="Seleccione cuenta receptora" /></SelectTrigger>
                <SelectContent>
                  {cuentas.length === 0
                    ? <SelectItem disabled value="_empty">Sin cuentas registradas</SelectItem>
                    : cuentas.map(c => <SelectItem key={c.id} value={c.id}>{c.banco_nombre} · {c.numero_cuenta} · {tipoCuentaLabel(c.tipo)}{c.rif ? ` · ${c.rif}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nº Referencia* <span className="text-[10px] text-slate-400">· mín. 6 caracteres</span></Label>
              <Input
                data-testid="portal-ref"
                value={form.referencia}
                onChange={e => setForm({...form, referencia:e.target.value})}
                placeholder="123456789"
                minLength={6}
                aria-invalid={!!form.referencia && String(form.referencia).trim().length < 6}
                className={form.referencia && String(form.referencia).trim().length < 6 ? "border-red-400 focus-visible:ring-red-400" : ""}
              />
              {form.referencia && String(form.referencia).trim().length < 6 && (
                <p className="text-[11px] text-red-600 mt-1">La referencia debe tener al menos 6 caracteres.</p>
              )}
            </div>
            <div><Label>Fecha del pago*</Label><Input type="date" value={form.fecha_pago} onChange={e => setForm({...form, fecha_pago:e.target.value})} /></div>
            <div><Label>Monto pagado*</Label><Input data-testid="portal-monto" type="number" step="0.01" value={form.monto_pagado || totalSel} onChange={e => setForm({...form, monto_pagado:e.target.value})} /></div>
            <div>
              <Label>Comprobante de Pago* <span className="text-[10px] text-slate-400">· JPG/PNG/PDF · máx 8 MB · obligatorio</span></Label>
              <div className={`flex items-center gap-2 p-2 rounded-md border ${form.comprobante_url ? "border-emerald-200 bg-emerald-50/50" : "border-red-200 bg-red-50/40"}`}>
                <input id="portal-comprobante-input" type="file" accept="image/jpeg,image/png,application/pdf" className="hidden" onChange={e => handleUpload(e.target.files?.[0])} data-testid="portal-comprobante-input" />
                <Button type="button" variant="outline" disabled={uploading} onClick={() => document.getElementById("portal-comprobante-input").click()} data-testid="portal-comprobante-btn"><Upload className="w-4 h-4 mr-1.5" />{uploading ? "Subiendo..." : (form.comprobante_url ? "Cambiar archivo" : "Subir archivo")}</Button>
                {form.comprobante_url ? (
                  <a href={fullComprobanteUrl(form.comprobante_url)} target="_blank" rel="noopener noreferrer" className="text-orange-600 text-xs inline-flex items-center gap-1 underline hover:text-orange-700" data-testid="portal-comprobante-link"><FileText className="w-3.5 h-3.5" />Ver archivo</a>
                ) : (
                  <span className="text-[11px] text-red-700 font-medium">Archivo requerido</span>
                )}
              </div>
            </div>
            <div className="md:col-span-2"><Label>Observaciones</Label><Textarea value={form.observaciones} onChange={e => setForm({...form, observaciones:e.target.value})} rows={3} /></div>
          </div>
          <div className="flex justify-end mt-4">
            <Button data-testid="btn-enviar-pago" onClick={submit} className="bg-orange-500 hover:bg-orange-600 text-white px-6">
              <CheckCircle2 className="w-4 h-4 mr-2" />Enviar reporte de pago
            </Button>
          </div>
        </Card>
      )}

      {/* Modal de confirmación tras reportar pago */}
      <Dialog open={confirmOpen} onOpenChange={(o) => {
        setConfirmOpen(o);
        if (!o) { navigate("/portal/historial"); }
      }}>
        <DialogContent className="max-w-md" data-testid="portal-pago-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-900">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
              ¡Reporte enviado con éxito!
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {confirmNro && (
              <div className="text-xs text-slate-500">
                N° de comprobante: <span className="font-mono font-bold text-green-900">{confirmNro}</span>
              </div>
            )}
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-3">
              <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-slate-800 leading-relaxed">
                <span className="font-semibold text-amber-900">Apreciado contribuyente</span>, le informamos que su pago será validado en un lapso <span className="font-semibold">menor a 24 horas</span>.
              </p>
            </div>
            <p className="text-xs text-slate-500">
              Puede consultar el estado de su pago en la sección <span className="font-semibold text-green-800">Historial</span>.
            </p>
          </div>
          <DialogFooter>
            <Button
              onClick={() => { setConfirmOpen(false); navigate("/portal/historial"); }}
              className="bg-green-700 hover:bg-green-800 text-white w-full"
              data-testid="portal-pago-confirm-btn"
            >
              Ir a Historial
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
